import { FormattedMessage, defineMessages } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import type { League } from '#src/components/landing/types.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';
import type { Color } from '#src/style/types.ts';

const Root = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1440 / 724;
  min-height: 420px;
  max-height: 80vh;
  overflow: hidden;
  background: var(--c-black);
`;

const Background = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

const Content = styled(Vertical).attrs({ center: true, gap: 4 })`
  position: absolute;
  inset: 0;
  justify-content: center;
  text-align: center;
  padding: 0 var(--double-unit);
  color: var(--text-color, var(--c-white));
`;

const Title = styled.h2`
  font-family: var(--brand-font);
  font-size: var(--fs-landing-title);
  line-height: 1;
  text-transform: uppercase;
  text-shadow: 0 4px 41.5px rgba(0, 0, 0, 0.9);
`;

const Amount = styled.span`
  background: linear-gradient(183deg, #ffffff 1%, #fcff63 37%, #c26900 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

const Description = styled(BodyM)`
  max-width: 794px;
`;

const messages = defineMessages({
  title: { defaultMessage: 'Win up to <gold>€1,000</gold>' },
  description: {
    defaultMessage:
      'Is your team performing? Climb the tiers and win up to €1,000 in rewards!',
  },
});

type Props = {
  league: League;
  background?: string;
  color?: Color;
};

export const RewardBanner = ({ league, background, color }: Props) => {
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const onClick = () => {
    track('Click Landing Page Win', { clickSource: league });
    signUp();
  };

  return (
    <Root>
      {!!background && <Background src={background} alt="" />}
      <Content style={{ '--text-color': color } as CSSProperties}>
        <Title>
          <FormattedMessage
            {...messages.title}
            values={{ gold: chunks => <Amount>{chunks}</Amount> }}
          />
        </Title>
        <Description text-center>
          <FormattedMessage {...messages.description} />
        </Description>
        <Button color="secondary" size="large" onClick={onClick}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </Content>
    </Root>
  );
};
